import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from 'views/components';
import {translate} from 'views/module';

import {ENTITY_TYPES} from './utils';

import moment from 'moment';

const DELIVERY_COLORS = {
  READY: 'default',
  SENDING: 'info',
  SIMULATING: 'info',
  SENT: 'success',
  WOULD_SEND: 'success',
  FAILED: 'error',
  SKIPPED: 'warning',
  CANCELED: 'default',
};

const getLatestMessageText = (message) => {
  const text = String(message?.body?.blob || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 120) {
    return text;
  }

  return `${text.slice(0, 117)}...`;
};

const RecipientTable = (props) => {
  const {
    channel,
    deliveryById,
    entityType,
    recipientPages,
  } = props;
  const recipients = recipientPages.reduce((output, page) => [...output, ...page], []);

  return (
    <TableContainer component={Paper} sx={{maxHeight: '58vh', border: '1px solid', borderColor: 'divider'}}>
      <Table stickyHeader size='small'>
        <TableHead>
          <TableRow>
            <TableCell>{translate('bulkMessaging.Table.Name')}</TableCell>
            <TableCell>{translate('bulkMessaging.Table.ResourceRn')}</TableCell>
            <TableCell>{translate('bulkMessaging.Table.Status')}</TableCell>
            <TableCell>{translate('bulkMessaging.Table.Recipient')}</TableCell>
            <TableCell>{translate('bulkMessaging.Table.Contact')}</TableCell>
            {
              entityType === ENTITY_TYPES.CLIENT &&
              <TableCell>{translate('bulkMessaging.Table.LatestMessage')}</TableCell>
            }
            <TableCell>{translate('bulkMessaging.Table.Delivery')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            recipients.map((recipient) => {
              const entityPath = entityType === ENTITY_TYPES.CAREGIVER ? 'caregiver' : 'client';
              const statusExpression = `${entityPath}.Status.${recipient.status}.Title`;
              const delivery = deliveryById[recipient.id];
              const deliveryStatus = delivery?.status ||
                (channel && !recipient.eligibilityPending ? recipient.eligible ? 'READY' : 'SKIPPED' : null);
              const latestMessage = recipient.item.clientLastMessage;
              const latestMessageText = getLatestMessageText(latestMessage);

              return (
                <TableRow key={recipient.id} hover>
                  <TableCell sx={{minWidth: 160}}>
                    <Link href={`/${entityPath}/${recipient.resourceRn}`} underline='hover'>
                      {recipient.entityName}
                    </Link>
                  </TableCell>
                  <TableCell sx={{maxWidth: 250, wordBreak: 'break-all'}}>
                    <Typography sx={{fontSize: '12px'}}>{recipient.resourceRn}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={translate(statusExpression)} size='small' />
                  </TableCell>
                  <TableCell sx={{minWidth: 160}}>
                    {recipient.recipientName || '-'}
                  </TableCell>
                  <TableCell sx={{minWidth: 190}}>
                    <Typography sx={{fontSize: '13px', wordBreak: 'break-word'}}>
                      {recipient.contact || (channel && !recipient.eligibilityPending ?
                        translate('bulkMessaging.Table.NoContact') : '-')}
                    </Typography>
                    {
                      recipient.skipReason && channel &&
                      <Typography sx={{fontSize: '11px', color: 'error.main', mt: 0.5}}>
                        {translate(`bulkMessaging.SkipReasons.${recipient.skipReason}`)}
                      </Typography>
                    }
                  </TableCell>
                  {
                    entityType === ENTITY_TYPES.CLIENT &&
                    <TableCell sx={{minWidth: 240, maxWidth: 360}}>
                      {
                        latestMessageText ?
                          <Box>
                            <Typography sx={{fontSize: '13px', wordBreak: 'break-word'}}>
                              {latestMessageText}
                            </Typography>
                            {
                              latestMessage?.createdAt &&
                              <Typography sx={{fontSize: '11px', color: 'text.secondary', mt: 0.5}}>
                                {moment(latestMessage.createdAt).format('MMM D, YYYY h:mm A')}
                              </Typography>
                            }
                          </Box> :
                          <Typography sx={{fontSize: '13px', color: 'text.secondary'}}>
                            {translate('bulkMessaging.Table.NoMessage')}
                          </Typography>
                      }
                    </TableCell>
                  }
                  <TableCell>
                    <Stack spacing={0.5} alignItems='flex-start'>
                      {
                        deliveryStatus ?
                        <Chip
                          color={DELIVERY_COLORS[deliveryStatus]}
                          label={translate(`bulkMessaging.Delivery.${deliveryStatus}`)}
                          size='small'
                        /> :
                        '-'
                      }
                      {
                        delivery?.failureCode &&
                        <Typography sx={{fontSize: '11px', color: 'error.main'}}>
                          {translate(`bulkMessaging.FailureReasons.${delivery.failureCode}`)}
                        </Typography>
                      }
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

RecipientTable.propTypes = {
  channel: PropTypes.string,
  deliveryById: PropTypes.object.isRequired,
  entityType: PropTypes.oneOf(Object.values(ENTITY_TYPES)).isRequired,
  recipientPages: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
};

export default React.memo(RecipientTable);
